import { pushCallDebugLog } from 'vcs-common';
/**
 * Периодическая проверка «а звук вообще идёт?» поверх RTCStats.
 *
 * События LiveKit (publish/subscribe/mute) показывают только намерения: трек
 * опубликован, подписка создана, участник не в муте. Они остаются зелёными и
 * тогда, когда микрофон отдаёт тишину или входящие пакеты не приходят вовсе —
 * то есть ровно в тех случаях, когда «человека не слышно».
 *
 * Здесь берутся реальные счётчики WebRTC (bytesSent/bytesReceived, audioLevel)
 * из уже работающего таймера статистики и в панель пишется только значимое:
 * появление проблемы, её исчезновение и редкий heartbeat «всё в порядке».
 */
/** Ниже этого уровня микрофон считается отдающим тишину. */
const SILENT_AUDIO_LEVEL = 0.001;
/** Сколько подряд «плохих» замеров нужно, чтобы сообщить о проблеме (тик = 5 c). */
const PROBLEM_TICKS = 2;
/** Как часто писать сводку, когда всё хорошо (тик = 5 c). */
const HEARTBEAT_TICKS = 6;
function readOutboundAudio(report) {
    if (!report)
        return null;
    let bytesSent = null;
    let audioLevel = null;
    report.forEach((stat) => {
        const kind = (stat['kind'] ?? stat['mediaType']);
        if (stat.type === 'outbound-rtp' && kind === 'audio') {
            bytesSent = (bytesSent ?? 0) + Number(stat['bytesSent'] ?? 0);
        }
        // media-source отражает то, что реально снимается с микрофона, ещё до кодека
        if (stat.type === 'media-source' && kind === 'audio') {
            const level = stat['audioLevel'];
            if (typeof level === 'number')
                audioLevel = Math.max(audioLevel ?? 0, level);
        }
    });
    return bytesSent === null ? null : { bytesSent, audioLevel };
}
function readInboundAudio(report, trackIdToIdentity) {
    const result = new Map();
    if (!report)
        return result;
    report.forEach((stat) => {
        const kind = (stat['kind'] ?? stat['mediaType']);
        if (stat.type !== 'inbound-rtp' || kind !== 'audio')
            return;
        const trackIdentifier = String(stat['trackIdentifier'] ?? '');
        const key = String(stat['ssrc'] ?? stat.id);
        result.set(key, {
            identity: trackIdToIdentity.get(trackIdentifier) ?? `ssrc:${key}`,
            bytesReceived: Number(stat['bytesReceived'] ?? 0),
            packetsReceived: Number(stat['packetsReceived'] ?? 0),
            packetsLost: Number(stat['packetsLost'] ?? 0),
        });
    });
    return result;
}
function buildTrackIdToIdentity(room) {
    const map = new Map();
    room.remoteParticipants.forEach((participant) => {
        participant.audioTrackPublications.forEach((publication) => {
            const mediaStreamTrackId = publication.track?.mediaStreamTrack?.id;
            if (mediaStreamTrackId)
                map.set(mediaStreamTrackId, participant.identity);
        });
    });
    return map;
}
/**
 * onMicrophoneUnheardChange(true) — микрофон включён, но данные не уходят. Тишина по уровню
 * сигнала сюда не входит: молчащий участник в тихой комнате — это норма.
 * onMicrophoneUnheardChange(false) приходит на каждом замере, где звук реально уходит: так
 * снимается и предупреждение, поднятое снаружи (SDK увидел тишину при публикации дорожки).
 */
export function createAudioHealthLogger(onMicrophoneUnheardChange) {
    let tick = 0;
    let prevOutbound = null;
    const prevInbound = new Map();
    let outboundBadTicks = 0;
    let outboundProblemReported = false;
    let stalledTicks = 0;
    let stalledReported = false;
    const inboundBadTicks = new Map();
    const inboundProblemReported = new Set();
    return ({ publisherReport, subscriberReport, room, isMicrophoneMuted, hasMicrophone }) => {
        tick += 1;
        const trackIdToIdentity = buildTrackIdToIdentity(room);
        const outbound = readOutboundAudio(publisherReport);
        const inbound = readInboundAudio(subscriberReport, trackIdToIdentity);
        // --- исходящее: слышат ли меня остальные ---
        let outboundSummary = null;
        if (outbound && hasMicrophone) {
            const bytesDelta = prevOutbound ? outbound.bytesSent - prevOutbound.bytesSent : null;
            const silent = outbound.audioLevel !== null && outbound.audioLevel < SILENT_AUDIO_LEVEL;
            const notSending = bytesDelta !== null && bytesDelta <= 0;
            outboundSummary = {
                bytesSentDelta: bytesDelta,
                audioLevel: outbound.audioLevel,
                muted: isMicrophoneMuted,
            };
            // В муте тишина и остановка потока — норма, проблемой это не считаем
            const bad = !isMicrophoneMuted && (silent || notSending);
            outboundBadTicks = bad ? outboundBadTicks + 1 : 0;
            if (outboundBadTicks >= PROBLEM_TICKS && !outboundProblemReported) {
                outboundProblemReported = true;
                pushCallDebugLog('audio-health', notSending
                    ? 'Мой микрофон не отправляет данные — меня не слышат'
                    : 'Мой микрофон отдаёт тишину — меня не слышат', outboundSummary, 'error');
            }
            else if (!bad && outboundProblemReported) {
                outboundProblemReported = false;
                pushCallDebugLog('audio-health', 'Исходящее аудио восстановилось', outboundSummary);
            }
            stalledTicks = !isMicrophoneMuted && notSending ? stalledTicks + 1 : 0;
            const isStalled = stalledTicks >= PROBLEM_TICKS;
            const isAudible = !isMicrophoneMuted && bytesDelta !== null && !notSending && outbound.audioLevel !== null && !silent;
            if (isStalled !== stalledReported) {
                stalledReported = isStalled;
                onMicrophoneUnheardChange?.(isStalled);
            }
            else if (isAudible) {
                onMicrophoneUnheardChange?.(false);
            }
            prevOutbound = outbound;
        }
        // --- входящее: слышу ли я каждого участника ---
        const inboundSummary = [];
        inbound.forEach((current, key) => {
            const previous = prevInbound.get(key);
            const bytesDelta = previous ? current.bytesReceived - previous.bytesReceived : null;
            const lostDelta = previous ? current.packetsLost - previous.packetsLost : null;
            const entry = {
                identity: current.identity,
                bytesReceivedDelta: bytesDelta,
                packetsLostDelta: lostDelta,
            };
            inboundSummary.push(entry);
            const participant = Array.from(room.remoteParticipants.values()).find((p) => p.identity === current.identity);
            const remoteMuted = participant
                ? Array.from(participant.audioTrackPublications.values()).every((publication) => publication.isMuted)
                : false;
            const bad = !remoteMuted && bytesDelta !== null && bytesDelta <= 0;
            const badTicks = bad ? (inboundBadTicks.get(key) ?? 0) + 1 : 0;
            inboundBadTicks.set(key, badTicks);
            if (badTicks >= PROBLEM_TICKS && !inboundProblemReported.has(key)) {
                inboundProblemReported.add(key);
                pushCallDebugLog('audio-health', `Не приходит аудио от ${current.identity} — я его не слышу`, entry, 'error');
            }
            else if (!bad && inboundProblemReported.has(key)) {
                inboundProblemReported.delete(key);
                pushCallDebugLog('audio-health', `Аудио от ${current.identity} восстановилось`, entry);
            }
            prevInbound.set(key, current);
        });
        // Участник ушёл — снимаем накопленное состояние, чтобы не «чинить» его задним числом
        Array.from(prevInbound.keys()).forEach((key) => {
            if (inbound.has(key))
                return;
            prevInbound.delete(key);
            inboundBadTicks.delete(key);
            inboundProblemReported.delete(key);
        });
        if (tick % HEARTBEAT_TICKS === 0) {
            pushCallDebugLog('audio-health', `Аудио: исходящее и ${inboundSummary.length} входящих потоков`, {
                outbound: outboundSummary,
                inbound: inboundSummary,
            });
        }
    };
}
